import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { Task } from '@/types/lead';
import { Bell, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface TaskNotificationsProps {
  tasks: Task[];
  setActiveTab: (tab: string) => void;
  className?: string;
}

const TaskNotifications: React.FC<TaskNotificationsProps> = ({ tasks, setActiveTab, className }) => {
  // Optimized: Filter and sort tasks using useMemo instead of fetching
  const relevantTasks = useMemo(() => {
    const now = new Date();
    const filtered = tasks.filter(task => 
      task.status === 'pending' && 
      task.dueDate && 
      new Date(task.dueDate) > now
    );
    
    filtered.sort((a, b) => {
      const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
      const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
      return dateA - dateB;
    });
    
    return filtered.slice(0, 3);
  }, [tasks]);
  
  const overdueTasksCount = useMemo(() => {
    return relevantTasks.filter(task => {
      const dueDate = task.dueDate ? new Date(task.dueDate) : null;
      return dueDate && dueDate < new Date();
    }).length;
  }, [relevantTasks]);
  
  const upcomingTasksCount = useMemo(() => {
    return relevantTasks.filter(task => {
      const dueDate = task.dueDate ? new Date(task.dueDate) : null;
      if (!dueDate) return false;
      
      const now = new Date();
      const tomorrow = new Date();
      tomorrow.setHours(now.getHours() + 24);
      
      return dueDate >= now && dueDate <= tomorrow;
    }).length;
  }, [relevantTasks]);
  
  if (relevantTasks.length === 0) {
    return null;
  }
  
  return (
    <Card className={className}>
      <CardHeader className="space-y-1 pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">Task Reminders</CardTitle>
          {(overdueTasksCount > 0 || upcomingTasksCount > 0) && (
            <div className="flex space-x-2">
              {overdueTasksCount > 0 && (
                <Badge variant="destructive" className="flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {overdueTasksCount} Overdue
                </Badge>
              )}
              {upcomingTasksCount > 0 && (
                <Badge variant="outline" className="flex items-center bg-amber-50 text-amber-600 border-amber-200">
                  <Bell className="h-3 w-3 mr-1" />
                  {upcomingTasksCount} Due Soon
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {relevantTasks.map(task => {
          const dueDate = task.dueDate ? new Date(task.dueDate) : null;
          const isOverdue = dueDate && dueDate < new Date();
          
          return (
            <div key={task.id} className="text-sm">
              <div className="flex items-start justify-between">
                <p className={`font-medium ${isOverdue ? 'text-destructive' : ''}`}>{task.title}</p>
                <span className={`text-xs ${isOverdue ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {dueDate ? format(dueDate, "MMM d") : 'No date'}
                </span>
              </div>
              {task.description && (
                <p className="text-xs text-muted-foreground truncate">{task.description}</p>
              )}
            </div>
          );
        })}
        
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full mt-2"
          onClick={() => setActiveTab('tasks')}
        >
          View All Tasks
        </Button>
      </CardContent>
    </Card>
  );
};

export default TaskNotifications;
